import { Box } from "@mui/material";
import { ReactNode, useMemo } from "react";
import { DEFAULT_VIDEO_ASPECT_RATIO } from "../constants";

export type VideoGridLayout =
  | {
      columnsPerRow?: number;
      maxTileHeight?: string;
      minTileWidth?: never;
    }
  | {
      minTileWidth: string;
      maxTileHeight?: string;
      columnsPerRow?: never;
    };

type VideoGridProps = Readonly<{ children: ReactNode } & VideoGridLayout>;

const GAP_PX = 8;

export const maxTileWidthForHeight = (maxTileHeight: string | undefined) =>
  maxTileHeight === undefined
    ? "100%"
    : `min(100%, calc(${maxTileHeight} * ${DEFAULT_VIDEO_ASPECT_RATIO}))`;

export const VideoGrid = (props: VideoGridProps) => {
  const columnsPerRow = props.columnsPerRow ?? 3;
  const flexBasis = useMemo(() => {
    const percentage = 100 / columnsPerRow;
    return `calc(${percentage}% - ${GAP_PX}px)`;
  }, [columnsPerRow]);

  if (props.minTileWidth !== undefined) {
    return (
      <Box
        data-testid="video-grid"
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(min(${props.minTileWidth}, 100%), 1fr))`,
          gap: `${GAP_PX}px`,
          justifyItems: "center",
          "& > *": {
            width: "100%",
            minWidth: 0,
            maxWidth: maxTileWidthForHeight(props.maxTileHeight),
          },
        }}
      >
        {props.children}
      </Box>
    );
  }

  return (
    <Box
      data-testid="video-grid"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent:
          props.maxTileHeight !== undefined ? "center" : undefined,
        gap: `${GAP_PX}px`,
        "& > *": {
          flex: `1 1 ${flexBasis}`,
          minWidth: "min(300px, 100%)",
          maxWidth:
            props.maxTileHeight !== undefined
              ? maxTileWidthForHeight(props.maxTileHeight)
              : "100%",
          "@media (min-width: 960px)": {
            maxWidth:
              props.maxTileHeight !== undefined
                ? maxTileWidthForHeight(props.maxTileHeight)
                : flexBasis,
          },
        },
      }}
    >
      {props.children}
    </Box>
  );
};
